<template>
  <section
    ref="container"
    class="app-state"
    :class="`app-state--${state}`"
    :data-app-state="state"
    :data-route-state="state"
    :role="semantics.role"
    :aria-live="semantics.live"
    :aria-labelledby="titleId"
  >
    <template v-if="state === 'loading'">
      <h2 :id="titleId" ref="heading" class="app-state__title">{{ resolvedTitle }}</h2>
      <p class="app-state__message">{{ resolvedMessage }}</p>
      <div class="app-state__skeleton" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </template>
    <template v-else>
      <q-icon class="app-state__icon" :name="definition.icon" aria-hidden="true" />
      <h2 :id="titleId" ref="heading" class="app-state__title" tabindex="-1">{{ resolvedTitle }}</h2>
      <p class="app-state__message">{{ resolvedMessage }}</p>
      <q-btn
        v-if="resolvedActionLabel"
        color="primary"
        no-caps
        :label="resolvedActionLabel"
        @click="handleAction"
      />
    </template>
  </section>
</template>

<script>
import { getCurrentInstance, nextTick } from 'vue'

const STATE_DEFINITIONS = Object.freeze({
  loading: {
    title: 'Загружаем данные',
    message: 'Это займет несколько секунд.',
    icon: 'hourglass_top',
    role: 'status',
    live: 'polite'
  },
  empty: {
    title: 'Данных пока нет',
    message: 'Здесь появятся данные после первого добавления.',
    icon: 'inbox',
    role: 'region',
    live: 'off'
  },
  'filtered-empty': {
    title: 'По заданным фильтрам ничего не найдено',
    message: 'Измените условия поиска или сбросьте фильтры.',
    icon: 'filter_alt_off',
    actionLabel: 'Сбросить фильтры',
    event: 'reset',
    role: 'region',
    live: 'off'
  },
  error: {
    title: 'Не удалось загрузить данные',
    message: 'Попробуйте повторить запрос.',
    icon: 'error_outline',
    actionLabel: 'Повторить',
    event: 'retry',
    role: 'alert',
    live: 'assertive'
  },
  'access-denied': {
    title: 'Недостаточно прав',
    message: 'Обратитесь к администратору организации или вернитесь на главную.',
    icon: 'shield',
    actionLabel: 'На главную',
    actionTo: { name: 'home' },
    role: 'region',
    live: 'off'
  },
  'not-found': {
    title: 'Страница не найдена',
    message: 'Проверьте ссылку или вернитесь на главную.',
    icon: 'search_off',
    actionLabel: 'На главную',
    actionTo: { name: 'home' },
    role: 'region',
    live: 'off'
  },
  unavailable: {
    title: 'Раздел пока недоступен',
    message: 'Функция еще не подключена.',
    icon: 'construction',
    actionLabel: 'На главную',
    actionTo: { name: 'home' },
    role: 'region',
    live: 'off'
  },
  offline: {
    title: 'Нет подключения',
    message: 'Проверьте соединение и повторите попытку.',
    icon: 'cloud_off',
    actionLabel: 'Повторить',
    event: 'retry',
    role: 'status',
    live: 'polite'
  }
})

export const APP_STATE_VARIANTS = Object.freeze(Object.keys(STATE_DEFINITIONS))

export default {
  name: 'AppState',
  emits: ['action', 'reset', 'retry'],
  props: {
    state: {
      type: String,
      required: true,
      validator: (value) => APP_STATE_VARIANTS.includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    actionLabel: {
      type: String,
      default: ''
    },
    actionTo: {
      type: [String, Object],
      default: null
    }
  },
  setup() {
    const instance = getCurrentInstance()
    return { titleId: `app-state-${instance.uid}-title` }
  },
  computed: {
    definition() {
      return STATE_DEFINITIONS[this.state]
    },
    semantics() {
      return { role: this.definition.role, live: this.definition.live }
    },
    resolvedTitle() {
      return this.title || this.definition.title
    },
    resolvedMessage() {
      return this.message || this.definition.message
    },
    resolvedActionLabel() {
      return this.actionLabel || this.definition.actionLabel || ''
    },
    resolvedActionTo() {
      return this.actionTo || this.definition.actionTo || null
    }
  },
  watch: {
    state() {
      this.focusState()
    }
  },
  mounted() {
    this.focusState()
  },
  methods: {
    async focusState() {
      if (this.state === 'loading' || this.state === 'empty' || this.state === 'filtered-empty') return
      await nextTick()
      if (this.$refs.heading) this.$refs.heading.focus({ preventScroll: true })
    },
    handleAction() {
      if (this.definition.event) this.$emit(this.definition.event)
      this.$emit('action', this.state)
      if (this.resolvedActionTo) this.$router.push(this.resolvedActionTo)
    }
  }
}
</script>

<style scoped>
.app-state {
  --app-state-color: var(--sid-color-text-secondary);
  --app-state-surface: var(--sid-color-surface-subtle);
  display: flex;
  min-height: var(--sid-state-min-height);
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: var(--sid-space-5);
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-large);
  background: var(--app-state-surface);
  color: var(--sid-color-text-primary);
}

.app-state--error {
  --app-state-color: var(--sid-color-danger);
  --app-state-surface: var(--sid-color-danger-surface);
}

.app-state--access-denied,
.app-state--offline {
  --app-state-color: var(--sid-color-warning);
  --app-state-surface: var(--sid-color-warning-surface);
}

.app-state--unavailable,
.app-state--loading {
  --app-state-color: var(--sid-color-info);
  --app-state-surface: var(--sid-color-info-surface);
}

.app-state__icon {
  color: var(--app-state-color);
  font-size: 2.5rem;
}

.app-state__title {
  margin: var(--sid-space-3) 0 0;
  font-size: var(--sid-font-size-section);
  line-height: var(--sid-line-height-section);
}

.app-state__message {
  max-width: 42rem;
  margin: var(--sid-space-2) 0 var(--sid-space-4);
  color: var(--sid-color-text-secondary);
}

.app-state__skeleton {
  display: grid;
  width: min(100%, 38rem);
  gap: var(--sid-space-3);
  margin-top: var(--sid-space-4);
}

.app-state__skeleton span {
  display: block;
  height: var(--sid-space-4);
  border-radius: var(--sid-radius-control);
  background: var(--sid-color-line);
}

.app-state__skeleton span:nth-child(1) {
  width: 52%;
}

.app-state__skeleton span:nth-child(2) {
  width: 82%;
}

.app-state__skeleton span:nth-child(3) {
  width: 68%;
}
</style>
